import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRegistry, ServiceDog } from '../context/RegistryContext';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { QRCodeSVG } from 'qrcode.react';
import { useReactToPrint } from 'react-to-print';
import config from '../config';
import {
  Printer,
  X,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Utensils,
  ShoppingCart,
  Plane,
  Bus,
  Ship,
  Hotel,
  Building
} from 'lucide-react';

const RegistryCard = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getServiceDogById } = useRegistry();
  const { isAuthenticated } = useAuth(); // Use the useAuth hook

  const [dog, setDog] = useState<ServiceDog | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showOwnerInfo, setShowOwnerInfo] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const printCardRef = useRef<HTMLDivElement>(null); // Ref for the print-specific card

  useEffect(() => {
    if (!id) {
      setError('Invalid registry ID');
      setLoading(false);
      return;
    }

    const fetchDog = async () => {
      try {
        console.log("Fetching dog with ID for registry card:", id);
        const serviceDog = await getServiceDogById(id);

        if (!serviceDog) {
          console.log("No dog found with ID for registry card:", id);
          setError('Service dog not found');
        } else {
          console.log("Successfully retrieved dog for registry card:", serviceDog.registryNumber);
          setDog(serviceDog);
        }
      } catch (err) {
        console.error('Error fetching service dog for registry card:', err);

        // Create a placeholder dog object for temporary IDs
        if (id.startsWith('temp-')) {
          console.log("Creating placeholder for temporary ID in registry card:", id);
          const placeholderDog = {
            id: id,
            registryNumber: "TEMP-" + id.substring(5, 11),
            dogName: "Registration Pending",
            dogBreed: "Unknown",
            dogAge: "Unknown",
            dogPhoto: "",
            ownerName: "Registration Pending",
            ownerAddress: "Registration Pending",
            ownerPhone: "Registration Pending",
            ownerEmail: "Registration Pending",
            serviceType: "Service Dog",
            certificationFile: "",
            registrationDate: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            status: 'pending' as 'pending'
          };
          setDog(placeholderDog);
        } else {
          setError('An error occurred while retrieving the registry information. Your registration has been received, but there was a problem displaying the details.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDog();
  }, [id, getServiceDogById]);

  const handlePrint = useReactToPrint({
    contentRef: printCardRef,
    documentTitle: `Service Dog Registry Card - ${dog?.registryNumber}`,
    onBeforePrint: () => {
      setShowOwnerInfo(true); // Show owner info when printing
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setShowOwnerInfo(false); // Reset to user preference after printing
    },
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-primary-200 h-12 w-12 mb-4"></div>
          <div className="h-4 bg-primary-200 rounded w-32 mb-3"></div>
          <div className="h-3 bg-primary-100 rounded w-24"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8 animate-fade-in">
        <div className="flex items-start text-accent-600 mb-6">
          <AlertCircle size={28} className="mr-3 mt-0.5" />
          <div>
            <h1 className="text-xl font-bold">Registry Status</h1>
            <p className="text-neutral-600 mt-1">{error}</p>
            <p className="text-neutral-600 mt-3">
              Your registration has likely been submitted successfully, but there was an issue displaying the registry card.
              You can try refreshing this page or checking your registration status later using the lookup feature.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg shadow transition-colors"
          >
            Refresh Page
          </button>
          <button
            onClick={() => navigate('/lookup')}
            className="bg-white border border-primary-600 text-primary-600 hover:bg-primary-50 px-6 py-2 rounded-lg shadow-sm transition-colors"
          >
            Verify Registration
          </button>
          <button
            onClick={() => navigate('/')}
            className="bg-neutral-100 hover:bg-neutral-200 text-neutral-700 px-6 py-2 rounded-lg shadow-sm transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!dog) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-primary-800 mb-2 sm:mb-0">
          Service Dog Registry Card
        </h1>
        <div className="flex space-x-2 sm:space-x-4">
          {isAuthenticated && ( // Conditionally render the button
            <button
              onClick={() => {
                // Add a small delay to ensure content is ready
                setTimeout(handlePrint, 100);
              }}
              className="flex items-center bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow transition-colors text-sm sm:text-base"
            >
              <Printer size={16} className="mr-1 sm:mr-2" />
              Print Card
            </button>
          )}
          <button
            onClick={() => navigate('/lookup')}
            className="flex items-center bg-white border border-neutral-300 hover:bg-neutral-100 text-neutral-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-sm transition-colors text-sm sm:text-base"
          >
            <X size={16} className="mr-1 sm:mr-2" />
            Close
          </button>
        </div>
      </div>

      {/* Registry Card - On-screen Version */}
      <div
        ref={cardRef}
        className="bg-white rounded-lg shadow-md overflow-hidden border border-neutral-200 print:hidden relative"
      >
        {/* Card Header */}
        <div className="bg-primary-600 text-white p-3 sm:p-4 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between relative z-10">
          <div className="flex items-center justify-center sm:justify-start mb-2 sm:mb-0">
            <img
              src="/USSdogsLogo.png"
              alt="US Service Dogs Logo"
              className="h-8 sm:h-10 w-auto mr-2 sm:mr-3"
            />
            <div>
              <h2 className="text-lg sm:text-xl font-bold">US Service Dog Registry</h2>
              <p className="text-xs sm:text-sm text-primary-100">Official Registration Card</p>
            </div>
          </div>
          <div className="flex items-center justify-center sm:justify-start">
            <span className="bg-accent-600 text-white text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full mr-2">
              OFFICIAL
            </span>
            {dog.status === 'active' && (
              <span className="bg-success-600 text-white text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center">
                <CheckCircle2 size={10} className="mr-1" />
                VERIFIED
              </span>
            )}
            {dog.status === 'pending' && (
              <span className="bg-warning-600 text-white text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
                PENDING
              </span>
            )}
            {dog.status === 'revoked' && (
              <span className="bg-error-600 text-white text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
                REVOKED
              </span>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div className="p-3 sm:p-4 md:p-6 relative z-10">
          <h2 className="text-lg sm:text-xl font-bold text-accent-600 text-center mb-2 sm:mb-4">Service Animal</h2>
          <p className="text-xs sm:text-sm text-neutral-600 text-center mb-3 sm:mb-6">
            The ADA mandates Service animals and handler have full access to all public places. It's the law. The FHAct mandates that ESA Animals and Handler have reasonable accommodation, as required by Federal Law. Therapy animals should be granted access to public places where they provide psychological and physiological therapy to individuals
          </p>
          <div className="flex flex-col md:flex-row">
            {/* Dog Photo */}
            <div className="mb-4 sm:mb-6 md:mb-0 md:mr-6 md:w-1/3">
              <div className="bg-neutral-100 rounded-lg overflow-hidden border border-neutral-200 aspect-square max-w-[200px] mx-auto md:max-w-none">
                {dog.dogPhoto ? (
                  <img
                    src={dog.dogPhoto}
                    alt={dog.dogName}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">
                    No Photo Available
                  </div>
                )}
              </div>

              <div className="mt-3 sm:mt-4">
                <div className="flex justify-center">
                  <QRCodeSVG
                    value={`${config.verificationBaseUrl}/registry/${dog.id}`}
                    size={100}
                    bgColor={"#FFFFFF"}
                    fgColor={"#0A2463"}
                    level={"H"}
                    includeMargin={false}
                    className="sm:h-[120px] sm:w-[120px]"
                  />
                </div>
                <p className="text-center text-xs sm:text-sm text-neutral-500 mt-1 sm:mt-2 mb-2 sm:mb-4">
                  Scan to verify registration
                </p>

                {/* US Service Dogs Logo */}
                <div className="flex justify-center mt-1 sm:mt-2">
                  <img
                    src="/USSdogsLogo.png"
                    alt="US Service Dogs Logo"
                    className="w-36 sm:w-48 h-auto"
                  />
                </div>
              </div>
            </div>

            {/* Dog Information */}
            <div className="md:w-2/3">
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-bold text-primary-700 mb-1 sm:mb-2">Registry Information</h3>
                <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 sm:p-4">
                  <div className="flex justify-between items-center mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-neutral-600">Registry Number:</span>
                    <span className="font-bold text-primary-800 text-xs sm:text-sm">{dog.registryNumber}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-neutral-600">Registration Date:</span>
                    <span className="text-neutral-800 text-xs sm:text-sm">{formatDate(dog.registrationDate)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-neutral-600">Last Updated:</span>
                    <span className="text-neutral-800 text-xs sm:text-sm">{formatDate(dog.lastUpdated)}</span>
                  </div>
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-bold text-primary-700 mb-1 sm:mb-2">Dog Information</h3>
                <div className="bg-white border border-neutral-200 rounded-lg p-3 sm:p-4">
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <h4 className="text-xs sm:text-sm text-neutral-500 mb-0.5 sm:mb-1">Name</h4>
                      <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.dogName}</p>
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm text-neutral-500 mb-0.5 sm:mb-1">Breed</h4>
                      <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.dogBreed}</p>
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm text-neutral-500 mb-0.5 sm:mb-1">Age</h4>
                      <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.dogAge}</p>
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm text-neutral-500 mb-0.5 sm:mb-1">Service Type</h4>
                      <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.serviceType}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1 sm:mb-2">
                  <h3 className="text-base sm:text-lg font-bold text-primary-700">Owner Information</h3>
                  {!showOwnerInfo && (
                    <button
                      onClick={() => setShowOwnerInfo(!showOwnerInfo)}
                      className="text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full transition-colors print:hidden"
                    >
                      {showOwnerInfo ? 'Hide Details' : 'Show Details'}
                    </button>
                  )}
                </div>

                {showOwnerInfo ? (
                  <div className="bg-white border border-neutral-200 rounded-lg p-3 sm:p-4">
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-start">
                        <User size={14} className="text-neutral-500 mt-0.5 sm:mt-1 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div>
                          <h4 className="text-xs sm:text-sm text-neutral-500">Name</h4>
                          <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.ownerName}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <MapPin size={14} className="text-neutral-500 mt-0.5 sm:mt-1 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div>
                          <h4 className="text-xs sm:text-sm text-neutral-500">Address</h4>
                          <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.ownerAddress}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Phone size={14} className="text-neutral-500 mt-0.5 sm:mt-1 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div>
                          <h4 className="text-xs sm:text-sm text-neutral-500">Phone</h4>
                          <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.ownerPhone}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Mail size={14} className="text-neutral-500 mt-0.5 sm:mt-1 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div>
                          <h4 className="text-xs sm:text-sm text-neutral-500">Email</h4>
                          <p className="font-semibold text-neutral-800 text-xs sm:text-sm">{dog.ownerEmail}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 sm:p-4 text-center print:hidden">
                    <p className="text-neutral-600 text-xs sm:text-sm">Owner information is hidden for privacy. Click "Show Details" to view.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Full Access Required */}
          <div className="mt-4 sm:mt-6 text-center">
            <h3 className="text-base sm:text-lg font-bold text-accent-600 mb-2 sm:mb-4">FULL ACCESS REQUIRED BY LAW</h3>
            <div className="flex justify-center space-x-3 sm:space-x-6 text-primary-800">
              <Building size={24} className="sm:h-9 sm:w-9" />
              <Utensils size={24} className="sm:h-9 sm:w-9" />
              <ShoppingCart size={24} className="sm:h-9 sm:w-9" />
              <Plane size={24} className="sm:h-9 sm:w-9" />
              <Bus size={24} className="sm:h-9 sm:w-9" />
              <Ship size={24} className="sm:h-9 sm:w-9" />
              <Hotel size={24} className="sm:h-9 sm:w-9" />
            </div>
            <div className="flex justify-center items-center mt-3 sm:mt-6 space-x-2 sm:space-x-4">
              <img
                src="/ada_v2.png"
                alt="ADA Logo"
                className="h-8 sm:h-12 w-auto"
              />
              <div className="text-left">
                <p className="text-xs sm:text-sm font-bold text-primary-700">Americans with Disabilities Act</p>
                <p className="text-[10px] sm:text-xs text-neutral-600">Protected under Federal Law</p>
              </div>
            </div>
          </div>
        </div>

        {/* Card Footer */}
        <div className="bg-neutral-50 p-3 sm:p-4 text-[10px] sm:text-xs text-neutral-600 border-t border-neutral-200 relative z-10">
          <p className="mb-1 sm:mb-2">
            Under the "American's with Disability" Act (ADA), all businesses and organizations who serve the public must allow people with disabilities to bring their service animals in all areas of the facility where people are normally allowed to go. The ADA defines a service as dogs that is individually trained to do work or perform tasks for people with disabilities, including a physical, sensory, psychiatric, intellectual, or other mental disability. Service Dogs are considered service animals under the ADA regardless of whether they have been licensed or certified by state or local government.
          </p>
          <p className="mb-1 sm:mb-2">
            For questions concerning Service or Assistance's Animals, please visit www.ada.gov or call the US Department of Justice ADA info line at: (Voice) 800-514-0301 or (TDD) 800-514-0383
          </p>
          <p>This card serves as official documentation of the service dog's registration with the US Service Dog Registry. Registration does not confer additional rights beyond those already provided by applicable laws, including the Americans with Disabilities Act.</p>
          <div className="mt-1 sm:mt-2 flex items-center">
            <Calendar size={12} className="mr-1" />
            <span>Verification Date: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Registry Card - Print Version (Hidden by default) */}
      <div ref={printCardRef} className="hidden print:block print:m-0 print:p-0">
        {/* Front of Card */}
        <div
          className="w-[3.375in] h-[2.125in] border border-black p-1 text-[8px] font-sans relative overflow-hidden mb-4 print:page-break-after-avoid" // Standard ID card size
        >
          {/* Watermark */}
          <img
            src="/watermark2.jpg"
            alt="Watermark"
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />

          {/* Card Content */}
          <div className="relative z-10 h-full flex flex-col">
            {/* Header */}
            <div className="bg-red-600 text-white text-center font-bold py-0.5">
              RIGHTS PROTECTED UNDER U.S. FEDERAL LAW
            </div>

            {/* Title */}
            <div className="text-center font-bold text-red-800 text-xl leading-tight">
              <strong>MEDICAL SERVICE DOG</strong>
            </div>

            {/* Handler and Dog Info */}
            <div className="flex mt-0.5 flex-grow">
              {/* Dog Photo */}
              <div className="w-1/3 border border-neutral-400 overflow-hidden mr-1" style={{ height: "0.9in" }}>
                {dog.dogPhoto ? (
                  <img
                    src={dog.dogPhoto}
                    alt={dog.dogName}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-400 text-[6px]">
                    No Photo
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="w-2/3 flex flex-col">
                <div className="mb-0.5">
                  <div className="font-bold">Animal's Name</div>
                  <div>{dog.dogName}</div>
                  <div className="font-bold">U.S. SERVICE ANIMAL REGISTRY</div>
                  <div>{dog.registryNumber}</div>
                </div>
                <div>
                  <div className="font-bold">Registered Handler</div>
                  <div>{dog.ownerName}</div>
                  <div className="text-[6px]">
                    {dog.ownerPhone}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="flex flex-col mt-0.5">
              {/* Footer */}
              <div className="text-center text-[6px] leading-tight">
                <strong>DO NOT SEPERATE service animal from handler. They are to be transported TOGETHER.</strong>
              </div>

              {/* Red line with verification text */}
              <div className="bg-red-600 text-white text-center text-[6px] py-0.5">
                SCAN QR CODE BELOW TO VERIFY REGISTRATION
              </div>

              {/* Logo, QR Code and Icons */}
              <div className="flex justify-between items-center mt-0 mb-[11px]">
                {/* Bottom Left: US Service Dogs Logo */}
                <img
                  src="/USSdogsLogo.png"
                  alt="US Service Dogs Logo"
                  className="h-12 w-auto"
                />

                {/* Middle: Icons */}
                <div className="flex space-x-1 text-neutral-800">
                  <Building size={24} />
                  <Utensils size={24} />
                  <ShoppingCart size={24} />
                  <Plane size={24} />
                  <Bus size={24} />
                  <Ship size={24} />
                  <Hotel size={24} />
                </div>

                {/* Bottom Right: QR Code */}
                <div className="flex items-center justify-center mb-[1px]">
                  <QRCodeSVG
                    value={`${config.verificationBaseUrl}/registry/${dog.id}`}
                    size={38}
                    bgColor={"#FFFFFF"}
                    fgColor={"#0A2463"}
                    level={"H"}
                    includeMargin={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back of Card */}
        <div className="w-[3.375in] h-[2.125in] border border-black text-[8px] font-sans relative overflow-hidden print:page-break-before-avoid">
          {/* Red Header */}
          <div className="bg-red-600 text-white text-center font-bold py-1 text-lg">
            UNITED STATES FEDERAL LAW
          </div>

          {/* Main Content */}
          <div className="bg-blue-100 p-2 h-[calc(100%-2.5rem)]">
            {/* Legal Text */}
            <p className="text-[7px] leading-tight mb-2">
              The Handler named on this ID is lawfully authorized to have their service
              animal alongside of them at all times, per the Americans with Disability Act
              (ADA). All businesses must permit service animals to accompany people with
              disabilities in all areas where members of the public are allowed to go. The
              ADA defines a service animal as a dog that is individually trained to do work or
              perform tasks for a person with a disability. This definition does not affect or
              limit the broader definition of "assistance animal" under the Fair Housing Act
              or the broader definition of "service animal" under the Air Carrier Access Act.
            </p>

            {/* Verification URL */}
            <p className="font-bold text-[8px] mb-1">
              Verify Registration For {dog.dogName} At:
            </p>
            <p className="text-[7px] mb-2">
              {config.verificationBaseUrl}/registry/{dog.id}
            </p>

            {/* Contact Info and DOJ Seal */}
            <div className="flex justify-between items-center">
              <div className="w-2/3">
                <p className="text-[7px] leading-tight">
                  For questions concerning Service or Assistance Animals, please
                  visit www.ada.gov or call the US Department of Justice ADA info
                  line at: (Voice) 800-514-0301 or (TDD) 800-514-0383 - Monday
                  thru Wednesday & Friday: 9:30 AM to 5:30 PM EST, Thursday 12:30
                  PM to 5:30 PM EST.
                </p>
              </div>

              {/* DOJ Seal */}
              <div className="w-1/3 flex justify-end">
                <img
                  src="/dojLogo.png"
                  alt="Department of Justice Seal"
                  className="w-auto h-23"
                  style={{ maxHeight: '88px', marginTop: '-46px' }}
                  onError={(e) => {
                    console.log("Error loading DOJ logo");
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistryCard;
